import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authApi } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { TermsContent } from './TermsPage'

const schema = z.object({
  userId: z.string().min(4, '아이디는 4자 이상이어야 합니다').max(50),
  email: z.string().email('올바른 이메일을 입력해주세요'),
  password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다'),
  passwordConfirm: z.string(),
  name: z.string().min(1, '이름을 입력해주세요'),
  organization: z.string().optional(),
  termsAgreed: z.boolean().refine((v) => v === true, { message: '이용약관에 동의해주세요.' }),
}).refine((d) => d.password === d.passwordConfirm, {
  message: '비밀번호가 일치하지 않습니다',
  path: ['passwordConfirm'],
})
type FormData = z.infer<typeof schema>

export default function RegisterPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [error, setError] = useState('')
  const [userIdChecked, setUserIdChecked] = useState(false)
  const [userIdAvailable, setUserIdAvailable] = useState<boolean | null>(null)
  const [checkingUserId, setCheckingUserId] = useState(false)
  const [showTerms, setShowTerms] = useState(false)

  const { register, handleSubmit, getValues, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { termsAgreed: false },
  })

  const userIdValue = watch('userId')

  const handleUserIdChange = () => {
    setUserIdChecked(false)
    setUserIdAvailable(null)
  }

  const handleCheckUserId = async () => {
    const userId = getValues('userId')
    if (!userId || userId.length < 4) return
    setCheckingUserId(true)
    try {
      const available = await authApi.checkUserId(userId)
      setUserIdAvailable(available)
      setUserIdChecked(true)
    } catch {
      setUserIdAvailable(null)
    } finally {
      setCheckingUserId(false)
    }
  }

  const onSubmit = async (data: FormData) => {
    if (!userIdChecked || !userIdAvailable) {
      setError('아이디 중복 확인을 해주세요.')
      return
    }
    setError('')
    try {
      const res = await authApi.register({
        userId: data.userId,
        email: data.email,
        password: data.password,
        name: data.name,
        organization: data.organization,
      })
      login(res.token, { userId: res.userId, name: res.name, email: res.email, userLevel: res.userLevel ?? 'FREE' })
      navigate('/dashboard')
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '회원가입에 실패했습니다.')
    }
  }

  const otherFields: Array<{ key: keyof FormData; label: string; type?: string; placeholder: string }> = [
    { key: 'email', label: '이메일', type: 'email', placeholder: 'example@email.com' },
    { key: 'name', label: '이름', placeholder: '홍길동' },
    { key: 'organization', label: '소속사 (선택)', placeholder: '회사명' },
    { key: 'password', label: '비밀번호', type: 'password', placeholder: '8자 이상' },
    { key: 'passwordConfirm', label: '비밀번호 확인', type: 'password', placeholder: '비밀번호 재입력' },
  ]

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-extrabold text-gray-900">마고 3D</h1>
          <h2 className="mt-2 text-center text-sm text-gray-600">Reality Mesh Service</h2>
        </div>

        <form className="mt-8 space-y-6 bg-white p-8 rounded-xl shadow" onSubmit={handleSubmit(onSubmit)}>
          <h3 className="text-xl font-bold text-gray-800">회원가입</h3>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
          )}

          <div className="space-y-4">
            {/* 아이디 + 중복 확인 */}
            <div>
              <label className="block text-sm font-medium text-gray-700">아이디</label>
              <div className="flex gap-2 mt-1">
                <input
                  {...register('userId', { onChange: handleUserIdChange })}
                  type="text"
                  placeholder="4자 이상 영문/숫자"
                  className="input-field flex-1"
                />
                <button
                  type="button"
                  onClick={handleCheckUserId}
                  disabled={checkingUserId || !userIdValue || userIdValue.length < 4}
                  className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {checkingUserId ? '확인 중...' : '중복 확인'}
                </button>
              </div>
              {errors.userId && <p className="form-error">{errors.userId.message}</p>}
              {userIdChecked && userIdAvailable === true && (
                <p className="mt-1 text-xs text-green-600">사용 가능한 아이디입니다.</p>
              )}
              {userIdChecked && userIdAvailable === false && (
                <p className="mt-1 text-xs text-red-600">이미 사용 중인 아이디입니다.</p>
              )}
            </div>

            {otherFields.map(({ key, label, type = 'text', placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <input
                  {...register(key)}
                  type={type}
                  placeholder={placeholder}
                  className="input-field mt-1"
                />
                {errors[key] && <p className="form-error">{errors[key]?.message}</p>}
              </div>
            ))}
          </div>

          {/* 이용약관 동의 */}
          <div className="space-y-1">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="termsAgreed"
                {...register('termsAgreed')}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <label htmlFor="termsAgreed" className="text-sm text-gray-700 cursor-pointer select-none">
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  className="font-medium text-primary-600 hover:underline"
                >
                  서비스 이용약관
                </button>
                을 읽었으며 이에 동의합니다.
              </label>
            </div>
            {errors.termsAgreed && (
              <p className="form-error">{errors.termsAgreed.message}</p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? '처리 중...' : '회원가입'}
          </button>

          <p className="text-center text-sm text-gray-600">
            이미 계정이 있으신가요?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              로그인
            </Link>
          </p>
        </form>
      </div>

      {/* 이용약관 모달 */}
      {showTerms && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowTerms(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-base font-bold text-gray-900">서비스 이용약관</h2>
                <p className="text-xs text-gray-400 mt-0.5">가이아쓰리디 ㈜ · MagoRealityMesh</p>
              </div>
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label="닫기"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-5 flex-1">
              <TermsContent />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 shrink-0 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={() => {
                  setValue('termsAgreed', true, { shouldValidate: true })
                  setShowTerms(false)
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
              >
                동의하고 닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
